import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authApi } from "./authApi";
import { decodeJWT, safeJsonParse } from "../utils/authUtils";
// import { fetchRolePermissionsByRoleId } from "./rolePermissionSlice";

// Thunk for async login logic
export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ userEmail, userPassword, loginType }, { rejectWithValue }) => {
    try {
      const data = await authApi.login(userEmail, userPassword, loginType);
      return data;
    } catch (error) {
      return rejectWithValue(error.backendMessage); // ONLY backend message or null
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await authApi.logout();
      return null;
    } catch (error) {
      return rejectWithValue(error.backendMessage);
    }
  },
);

export const validateToken = createAsyncThunk(
  "auth/validateToken",
  async (_, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        return rejectWithValue(null); // Silent - no generic message
      }

      const decoded = decodeJWT(token);
      if (!decoded || !decoded.exp) {
        return rejectWithValue(null);
      }

      const now = Math.floor(Date.now() / 1000);
      if (decoded.exp < now) {
        const refreshResult = await authApi.refreshAccessToken();
        if (refreshResult.accessToken) {
          sessionStorage.setItem("authToken", refreshResult.accessToken);
          const newDecoded = decodeJWT(refreshResult.accessToken);
          if (newDecoded && newDecoded.exp > now) {
            return { valid: true, decoded: newDecoded };
          }
        }
        return rejectWithValue(null);
      }

      return { valid: true, decoded };
    } catch (error) {
      return rejectWithValue(error.backendMessage || null);
    }
  },
);

const storedToken = sessionStorage.getItem("authToken");
const storedUser = safeJsonParse(sessionStorage.getItem("authUser"), null);

const normalizeUsertype = (value) => {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && (n === 0 || n === 1) ? n : null;
};

const initialState = {
  user: storedUser,
  token: storedToken,
  // If a token exists, we must validate it before treating the user as authenticated.
  // This avoids a brief "auto-login" window on first render.
  isLoading: Boolean(storedToken),
  error: null,
  isAuthenticated: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      // Clear both sessionStorage and localStorage for consistency
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("refreshToken");
      sessionStorage.removeItem("authUser");
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("authUser");
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const data = action.payload?.data;
        const token = data?.accessToken;
        const refreshToken = data?.refreshToken;
        const requestedLoginType = normalizeUsertype(
          action.meta?.arg?.loginType,
        );
        const responseLoginType = normalizeUsertype(data?.loginType);

        // Decode JWT token to get user data
        let user = null;
        if (token) {
          const decoded = decodeJWT(token);
          if (decoded) {
            const decodedUsertype = normalizeUsertype(decoded.usertype);
            const usertypeNum =
              decodedUsertype ?? responseLoginType ?? requestedLoginType ?? 1;
            const isAdmin = usertypeNum === 1;
            user = {
              userid: decoded.userid,
              compid: decoded.compid,
              branid: decoded.branid,
              roleid: decoded.roleid,
              usertype: usertypeNum,
              isAdmin,
              name: decoded.name,
              email: decoded.email,
              rolename: decoded.rolename,
              compname: decoded.compname,
              braname: decoded.braname,
              Permission: decoded.Permission,
              permissions:
                typeof decoded.Permission === "string"
                  ? decoded.Permission.split(",")
                    .map((p) => p.trim())
                    .filter(Boolean)
                  : decoded.Permission || [],
              rolrides: Array.isArray(decoded.rolrides)
                ? decoded.rolrides
                : typeof decoded.rolrides === "string"
                  ? decoded.rolrides.startsWith("[") ||
                    decoded.rolrides.startsWith("{")
                    ? JSON.parse(decoded.rolrides)
                    : decoded.rolrides
                      .split(",")
                      .map((p) => p.trim())
                      .filter(Boolean)
                  : decoded.rolrides || [],
            };
          }
        }

        state.isLoading = false;

        if (token) {
          state.isAuthenticated = true;
          state.token = token;
          state.user = user;
          state.error = null;

          // Save to both for different interceptors (api.js vs crudApi.js vs authSlice.js)
          sessionStorage.setItem("authToken", token);
          sessionStorage.setItem("refreshToken", refreshToken);
          sessionStorage.setItem("authUser", JSON.stringify(user));
          localStorage.setItem("authToken", token);
          localStorage.setItem("refreshToken", refreshToken);
          localStorage.setItem("authUser", JSON.stringify(user));

          import("../store/store").then(({ store }) => {
            setTimeout(() => {
              store.dispatch({ type: "signalr/connect" });
            }, 500);
          });
        } else {
          state.isAuthenticated = false;
          state.error = null; // NO frontend fallback
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload; // backendMessage or null
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("authUser");
        sessionStorage.removeItem("refreshToken");
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
        localStorage.removeItem("refreshToken");

        // Disconnect SignalR on logout
        import("../store/store").then(({ store }) => {
          store.dispatch({ type: "signalr/disconnect" });
        });
      })
      .addCase(logoutUser.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isLoading = false;
      })
      .addCase(validateToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        // Update token/user from validated data
        const { decoded } = action.payload;
        state.token = sessionStorage.getItem("authToken");
        if (decoded) {
          const stored = safeJsonParse(
            sessionStorage.getItem("authUser"),
            null,
          );
          const decodedUsertype = normalizeUsertype(decoded.usertype);
          const storedUsertype = normalizeUsertype(
            stored?.usertype ?? stored?.loginType,
          );
          const usertypeNum = decodedUsertype ?? storedUsertype ?? 1;
          const isAdmin = usertypeNum === 1;
          state.user = {
            userid: decoded.userid,
            compid: decoded.compid,
            branid: decoded.branid,
            roleid: decoded.roleid,
            usertype: usertypeNum,
            isAdmin,
            name: decoded.name,
            email: decoded.email,
            rolename: decoded.rolename,
            compname: decoded.compname,
            braname: decoded.braname,
            Permission: decoded.Permission,
            permissions:
              typeof decoded.Permission === "string"
                ? decoded.Permission.split(",")
                  .map((p) => p.trim())
                  .filter(Boolean)
                : decoded.Permission || [],
            rolrides: Array.isArray(decoded.rolrides)
              ? decoded.rolrides
              : typeof decoded.rolrides === "string"
                ? decoded.rolrides.startsWith("[") ||
                  decoded.rolrides.startsWith("{")
                  ? JSON.parse(decoded.rolrides)
                  : decoded.rolrides
                    .split(",")
                    .map((p) => p.trim())
                    .filter(Boolean)
                : decoded.rolrides || [],
          };
        }
      })
      .addCase(validateToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload; // backendMessage or null
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("authUser");
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("authUser");
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export const selectUserPermissions = (state) =>
  state.auth.user?.permissions || [];

export default authSlice.reducer;
